import React from 'react';
import { Duration } from 'luxon';

import makeStyles from '@mui/styles/makeStyles';

import { Grid, Table, TableBody, TableRow, TableCell } from '@mui/material';

import DA from '../../store/ReduxAccess';

const useStyles = makeStyles((theme) => ({
  root: {},
  emptyRow: {
    height: '30px',
  },
  titleRow: {
    fontSize: '1.2rem',
    fontWeight: 600,
    background: '#ff8c6e',
  },
  tableBanner: {
    fontWeight: 600,
    background: '#ff8c6e',
  },
  tableColumnLabel: {
    textAlign: 'center',
    fontWeight: 600,
    background: '#d6d6d6',
  },
  tableTotalColumnLabel: {
    textAlign: 'center',
    fontWeight: 600,
    background: '#beebff',
  },
  tableRowLabel: {
    fontWeight: 600,
    background: '#d6d6d6',
  },
  normalTableCell: {
    textAlign: 'center',
  },
  boldTableCell: {
    fontWeight: 600,
    textAlign: 'center',
  },
  tableTotalRowLabel: {
    fontWeight: 600,
    background: '#beebff',
    textAlign: 'right',
  },
  totalTableCell: {
    fontWeight: 600,
    background: '#beebff',
    textAlign: 'center',
  },
  boldTotalTableCell: {
    fontWeight: 600,
    background: '#beebff',
    textAlign: 'center',
  },
}));

const ReportPreview = ({
  startDate,
  lastIncludedDate,
  allFacilities,
  selectedFacilities,
  sourceTypes,
  referralTriples,
}) => {
  const classes = useStyles();

  const firmInfo = DA.getLoggedInFirmInfo();
  const firmName = firmInfo ? firmInfo.firmName : 'The Haven at College';
  const limitDate = lastIncludedDate.plus(Duration.fromObject({ days: 1 }));

  const filteredTriples = referralTriples.filter(
    (trip) =>
      trip.date.valueOf() >= startDate.valueOf() &&
      trip.date.valueOf() < limitDate.valueOf() &&
      selectedFacilities.some((fac) => fac === trip.facility)
  );
  const nForFacility = (fac) => filteredTriples.filter((t) => t.facility === fac).length;
  const nColumns = selectedFacilities.length + 2;

  const emptyRow = <TableRow className={classes.emptyRow} />;
  const startDateString = startDate.toFormat('D'); // 'localized numeric date'
  const lastIncludedDateString = lastIncludedDate.toFormat('D'); // 'localized numeric date'
  const dateRange = `${startDateString} - ${lastIncludedDateString}`;
  const titleRow = (
    <TableRow>
      <TableCell className={classes.titleRow} colSpan={nColumns}>
        {firmName} Referral Report: {dateRange}
      </TableCell>
    </TableRow>
  );
  const subtable1TitleRow = (
    <TableRow>
      <TableCell className={classes.tableBanner} colSpan={2}>
        Treatment Opportunities Created
      </TableCell>
    </TableRow>
  );
  const facilitiesRow = (
    <TableRow>
      <TableCell />
      {selectedFacilities.map((fac, i) => (
        <TableCell className={classes.tableColumnLabel} key={i}>
          {fac}
        </TableCell>
      ))}
      <TableCell className={classes.tableTotalColumnLabel}>TOTAL</TableCell>
    </TableRow>
  );
  const subtable2TitleRow = (
    <TableRow>
      <TableCell className={classes.tableBanner} colSpan={2}>
        Referral Source Type
      </TableCell>
    </TableRow>
  );

  let newProspectRowRunningTotal = 0;
  const newProspectsRow = (
    <TableRow>
      <TableCell className={classes.tableRowLabel}>New Prospects</TableCell>
      {selectedFacilities.map((fac) => {
        const n = nForFacility(fac);
        newProspectRowRunningTotal += n;
        return (
          <TableCell key={fac} className={classes.normalTableCell}>
            {n}
          </TableCell>
        );
      })}
      <TableCell className={classes.boldTableCell}>{newProspectRowRunningTotal}</TableCell>
    </TableRow>
  );

  const subtable1TotalsRow = (
    <TableRow>
      <TableCell className={classes.tableTotalRowLabel}>TOTAL</TableCell>
      {selectedFacilities.map((fac) => {
        const n = nForFacility(fac);
        return (
          <TableCell key={fac} className={classes.totalTableCell}>
            {n}
          </TableCell>
        );
      })}
      <TableCell className={classes.boldTotalTableCell}>{newProspectRowRunningTotal}</TableCell>
    </TableRow>
  );

  const totalsPerFacility = {};
  selectedFacilities.forEach((fac) => (totalsPerFacility[fac] = 0));

  let rowTotal = 0;
  const createSourceTypeRow = (sourceType, i) => {
    rowTotal = 0;
    return (
      <TableRow key={i}>
        <TableCell className={classes.tableRowLabel}> {sourceType}</TableCell>
        {selectedFacilities.map((fac, j) => {
          const nOfSourceTypeForFacility = filteredTriples.filter(
            (rT) => rT.facility === fac && rT.sourceType === sourceType
          ).length;
          rowTotal += nOfSourceTypeForFacility;
          totalsPerFacility[fac] += nOfSourceTypeForFacility;
          return (
            <TableCell className={classes.normalTableCell} key={j}>
              {nOfSourceTypeForFacility}
            </TableCell>
          );
        })}
        <TableCell className={classes.boldTableCell}>{rowTotal}</TableCell>
      </TableRow>
    );
  };

  let bottomRowTotal = 0;
  const createSourceTypeTableTotalsRow = () => {
    return (
      <TableRow>
        <TableCell className={classes.tableTotalRowLabel}>TOTAL</TableCell>
        {selectedFacilities.map((fac) => {
          const n = totalsPerFacility[fac];
          bottomRowTotal += n;
          return (
            <TableCell className={classes.totalTableCell} key={fac}>
              {n}
            </TableCell>
          );
        })}
        <TableCell className={classes.boldTotalTableCell}>{bottomRowTotal}</TableCell>
      </TableRow>
    );
  };

  return (
    <Grid container spacing={2}>
      <Grid item md={12}>
        <Table>
          <TableBody>
            {emptyRow}
            {titleRow}
            {emptyRow}
            {emptyRow}
            {subtable1TitleRow}
            {facilitiesRow}
            {newProspectsRow}
            {subtable1TotalsRow}
            {emptyRow}
            {emptyRow}
            {subtable2TitleRow}
            {facilitiesRow}
            {sourceTypes.map((sT, i) => createSourceTypeRow(sT, i))}
            {createSourceTypeTableTotalsRow()}
          </TableBody>
        </Table>
      </Grid>
    </Grid>
  );
};

export default ReportPreview;
